import axios, { type AxiosResponse } from 'axios'

export interface UrvDepartment {
    id: string
    name: string
}

export interface UrvWorker {
    user_id: number
    name: string
    shift_start: string
    status: 'working' | 'late' | 'error' | 'absent' | 'shift_today'
    avatar: string
}

export interface UrvEmployees {
    on_shift: UrvWorker[]
    waiting_for: UrvWorker[]
}

interface UrvEnvelope<T> {
    status?: string
    body?: T
    msg?: string
}

export class UrvApiError extends Error {
    readonly isSessionInvalid: boolean

    constructor(message: string, isSessionInvalid = false) {
        super(message)
        this.name = 'UrvApiError'
        this.isSessionInvalid = isSessionInvalid
    }
}

const urvHttp = axios.create({
    baseURL: import.meta.env.VITE_API_BASE,
    withCredentials: true,
})

function getEnvelopeBody<T>(response: AxiosResponse<UrvEnvelope<T>>): T {
    const { data } = response

    if (data.status === 'error') {
        const message = data.msg || 'Не удалось выполнить запрос'
        throw new UrvApiError(message)
    }

    if (!data.body) {
        throw new UrvApiError('Сервер вернул неполный ответ')
    }

    return data.body
}

function createUrvApiError(error: unknown, fallbackMessage: string): UrvApiError {
    if (error instanceof UrvApiError) {
        return error
    }

    if (axios.isAxiosError(error)) {
        const status = error.response?.status
        const responseData = error.response?.data as UrvEnvelope<unknown> | undefined
        const message = responseData?.msg || fallbackMessage
        const isSessionInvalid = status === 401 || status === 403

        return new UrvApiError(message, isSessionInvalid)
    }

    return new UrvApiError(fallbackMessage)
}

export async function authorizeUrvKey(key: string): Promise<UrvDepartment> {
    let responseBody: { department?: UrvDepartment }

    try {
        responseBody = getEnvelopeBody<{ department?: UrvDepartment }>(
            await urvHttp.post('/api/auth-keys/auth/', { key }, { withCredentials: true }),
        )
    } catch (error) {
        throw createUrvApiError(error, 'Не удалось авторизовать ключ')
    }

    const department = responseBody.department
    if (!department || typeof department.id !== 'string' || typeof department.name !== 'string') {
        throw new UrvApiError('Сервер вернул неполный ответ')
    }

    return department
}

export async function getUrvQrSvg(departmentId: string): Promise<string> {
    let responseBody: { svg?: string }

    try {
        responseBody = getEnvelopeBody<{ svg?: string }>(
            await urvHttp.get(`/api/departments/${departmentId}/get-qr-svg/`, { withCredentials: true }),
        )
    } catch (error) {
        throw createUrvApiError(error, 'Не удалось получить QR-код')
    }

    if (typeof responseBody.svg !== 'string') {
        throw new UrvApiError('Сервер вернул неполный ответ')
    }

    return responseBody.svg
}

export async function getUrvEmployees(departmentId: string): Promise<UrvEmployees> {
    let responseBody: { data?: UrvEmployees }

    try {
        responseBody = getEnvelopeBody<{ data?: UrvEmployees }>(
            await urvHttp.post('/api/checkincheckout/users-in-department-info/', { id: departmentId }, { withCredentials: true }),
        )
    } catch (error) {
        throw createUrvApiError(error, 'Не удалось получить список сотрудников')
    }

    const data = responseBody.data
    if (!data || !Array.isArray(data.on_shift) || !Array.isArray(data.waiting_for)) {
        throw new UrvApiError('Сервер вернул неполный ответ')
    }

    return data
}
